import { z } from 'zod'
import { uuid } from './common.js'
import { latitude, longitude } from './items.js'
import { phoneE164 } from './participants.js'

/**
 * The advisor's own library of places.
 *
 * Deliberately the same field set as `place`, minus everything that ties a place
 * to a trip. Adding one to a trip copies these fields across — see the schema
 * note on why it is a copy and not a reference.
 */

/**
 * Coarse on purpose — see the enum's note. Fine enough to cut two hundred Paris
 * entries down to the twelve restaurants, not so fine that it stops being filled
 * in consistently.
 */
export const savedPlaceKind = z.enum([
  'restaurant',
  'hotel',
  'cafe',
  'bar',
  'museum',
  'shop',
  'activity',
  'other',
])
export type SavedPlaceKind = z.infer<typeof savedPlaceKind>

const savedPlaceFields = {
  /** "Paris", "Toscana", "Lisboa" — how she groups them when she talks. */
  destination: z.string().trim().min(1).max(120),
  kind: savedPlaceKind.default('other'),
  name: z.string().trim().min(1).max(200),
  address: z.string().max(500).nullish(),
  addressLocal: z.string().max(500).nullish(),
  lat: latitude.nullish(),
  lng: longitude.nullish(),
  website: z.url().max(2000).nullish(),
  phone: phoneE164.nullish(),
  notes: z.string().max(20_000).nullish(),
}

export const createSavedPlaceRequest = z.object(savedPlaceFields)
export type CreateSavedPlaceRequest = z.infer<typeof createSavedPlaceRequest>

export const updateSavedPlaceRequest = z.object(savedPlaceFields).partial()
export type UpdateSavedPlaceRequest = z.infer<typeof updateSavedPlaceRequest>

export const savedPlace = z.object({
  id: uuid,
  destination: z.string(),
  kind: savedPlaceKind,
  name: z.string(),
  address: z.string().nullable(),
  addressLocal: z.string().nullable(),
  lat: latitude.nullable(),
  lng: longitude.nullable(),
  website: z.string().nullable(),
  phone: z.string().nullable(),
  notes: z.string().nullable(),
})
export type SavedPlace = z.infer<typeof savedPlace>

/**
 * Putting a library place onto a trip.
 *
 * `dayId` is optional: a place added without one lands in "not yet scheduled",
 * which is where a lot of planning legitimately starts — she knows the
 * restaurant is going in before she knows which night.
 */
export const addSavedPlaceToTripRequest = z.object({
  savedPlaceId: uuid,
  dayId: uuid.nullish(),
})
export type AddSavedPlaceToTripRequest = z.infer<typeof addSavedPlaceToTripRequest>

/** The reverse: keeping a place she just built on a trip. */
export const saveTripPlaceRequest = z.object({
  placeId: uuid,
  destination: z.string().trim().min(1).max(120),
  kind: savedPlaceKind.default('other'),
})
export type SaveTripPlaceRequest = z.infer<typeof saveTripPlaceRequest>
